import {Divider, LinearProgress, List, ListItem, ListSubheader, Stack, Typography} from "@mui/joy";
import FullLogo from "../Logo";
import {Link, useLocation} from "react-router-dom";
import {sidebarMenus} from "../../values/sidebarMenus";
import {BarChart3, LogIn, LogOut, UserIcon} from "lucide-react";
import {useAuth} from "../../hooks/useAuth";
import {useTranslation} from "react-i18next";
import LanguageSwitch from "../LanguageSwitch";
import {TIER_LABELS, isSubscriptionActive, remainingAnalyses, usedAnalysesToday} from "../../values/plans";

const Sidebar = () => {
    return <Stack display={{md: "flex", xs: "none"}} minWidth={"16rem"} direction={"row"}>
        <SidebarContent/>
        <Divider orientation={"vertical"}/>
    </Stack>
}

export const SidebarContent = () => {
    const location = useLocation()
    const auth = useAuth()
    const {t} = useTranslation()

    const subscription = auth.user?.subscription
    const active = isSubscriptionActive(subscription)
    // Discounted for the day, like the API does: a stale tally would misreport the quota
    const usedToday = active ? usedAnalysesToday(subscription) : 0
    const dailyLimit = active ? subscription?.dailyPredictionsLimit || 0 : 0
    const usageRatio = dailyLimit ? Math.min(100, (usedToday / dailyLimit) * 100) : 0
    const limitReached = !!dailyLimit && usedToday >= dailyLimit
    const usageColor = limitReached ? "danger" : usageRatio >= 70 ? "warning" : "success"

    return <Stack height={"100dvh"} width={"100%"} sx={{backgroundColor: "#050e1c"}}>
        <Stack padding={2}>
            <Link to={"/"} style={{display: "flex"}} aria-label={"Score Vision"}>
                <FullLogo width={140}/>
            </Link>
        </Stack>

        <Divider/>

        <ListItem nested sx={{paddingBottom: 1}}>
            <ListSubheader sx={{marginX: 4, marginY: 1}}>{t("navAnalysisSection")}</ListSubheader>
            <List sx={{paddingX: 2, gap: 1.5}}>
                {sidebarMenus.map((menu, index, array) =>
                    <Link key={menu.route} to={menu.route} style={{textDecoration: "none"}}>
                        <ListItem data-selected={location.pathname === menu.route}
                                  sx={{
                                      width: "100%",
                                      borderRadius: 10,
                                      paddingX: 2,
                                      paddingY: 1.5,
                                      backgroundColor: location.pathname === menu.route ? "primary.100" : "transparent",
                                      borderColor: "primary.800",
                                      borderStyle: "solid",
                                      borderWidth: location.pathname === menu.route ? 1.5 : 0,
                                      color: location.pathname === menu.route ? "primary.800" : "white",
                                      "&:hover": location.pathname === menu.route ? null : {
                                          backgroundColor: "rgba(255,255,255,0.1)",
                                      }
                                  }}>
                            <Stack alignItems={"center"} direction={"row"} gap={1}>
                                {menu.icon}
                                <Typography fontWeight={"600"} level={"title-md"} sx={{color: "inherit"}}>
                                    {t(menu.labelKey)}
                                </Typography>
                            </Stack>

                        </ListItem>
                    </Link>)}
            </List>
        </ListItem>

        <Divider/>

        {/* Quota of the signed in account, there is nothing to count for a visitor */}
        {auth.user && <>
            <Stack padding={3} gap={0.5}>
                <Typography textColor={"text.tertiary"} level={"body-xs"} startDecorator={<BarChart3/>}>
                    {t("sidebarTodayAnalysis")}
                </Typography>
                <Typography level={"h3"} textColor={`${usageColor}.400`}>{usedToday}/{dailyLimit || "—"}</Typography>
                <LinearProgress determinate={true} value={usageRatio} color={usageColor}/>
                <Typography level={"body-xs"} textColor={`${usageColor}.400`}>
                    {!active
                        ? t("sidebarNoPlan")
                        : limitReached
                            ? t("sidebarLimitReached")
                            : t("sidebarLeftToday", {count: remainingAnalyses(subscription)})}
                </Typography>
            </Stack>

            <Divider/>
        </>}


        <List sx={{paddingX: 2, gap: 1}}>
            {auth.user ? <>
                <Link to={"/account"} style={{textDecoration: "none"}}>
                    <ListItem data-selected={location.pathname === "/account"}
                              sx={{
                                  borderRadius: 10,
                                  backgroundColor: location.pathname === "/account" ? "rgba(255,255,255,0.1)" : "transparent",
                                  "&:hover": location.pathname === "/account" ? null : {
                                      backgroundColor: "rgba(255,255,255,0.1)",
                                  }
                              }}>

                        <Stack direction={"row"} gap={1} alignItems={"center"}>
                            <UserIcon style={{fontSize: 24, color: 'white'}}/>
                            <Stack overflow={"hidden"}>
                                <Typography textColor={"white"} level={"body-sm"} noWrap>
                                    {auth.user.name}
                                </Typography>
                                <Typography level={"body-sm"} textColor={"text.tertiary"}>
                                    {TIER_LABELS[auth.user.subscription?.tier || "free"]}
                                </Typography>
                            </Stack>

                        </Stack>

                    </ListItem>
                </Link>

                <ListItem onClick={() => auth.signOut?.()}
                          sx={{
                              borderRadius: 10,
                              cursor: "pointer",
                              paddingY: 1,
                              color: "grey",
                              "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.1)",
                                  color: "white"
                              }
                          }}>
                    <Typography startDecorator={<LogOut style={{color: 'inherit'}}/>}
                                sx={{
                                    color: "inherit",
                                }}>
                        {t("signOut")}
                    </Typography>
                </ListItem>
            </> : <ListItem onClick={() => auth.requestSignIn?.()}
                            sx={{
                                borderRadius: 10,
                                cursor: "pointer",
                                paddingY: 1,
                                color: "white",
                                "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.1)",
                                }
                            }}>
                <Typography startDecorator={<LogIn style={{color: 'inherit'}}/>} sx={{color: "inherit"}}>
                    {t("signIn")}
                </Typography>
            </ListItem>}

        </List>

        <Stack paddingX={2} paddingBottom={2} paddingTop={1}>
            <LanguageSwitch/>
        </Stack>

    </Stack>
}

export default Sidebar